import "@/App.css";
import "@/i18n";  // side-effect init — must load before any component uses useTranslation
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { DesignProvider } from "@/design";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import StationDash from "@/pages/StationDash";
import AdVerify from "@/pages/AdVerify";
import ClaimStation from "@/pages/ClaimStation";
import VerifyCertificate from "@/pages/VerifyCertificate";
import PlanningTool from "@/pages/PlanningTool";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import MapDashboard from "@/pages/MapDashboard";
import Listen from "@/pages/Listen";
import WatchTV from "@/pages/WatchTV";
import Demo from "@/pages/Demo";
import Billing from "@/pages/Billing";
import AdminPanel from "@/pages/AdminPanel";
import Profile from "@/pages/Profile";
import Legal from "@/pages/Legal";
import Methodology from "@/pages/Methodology";
import Crawler from "@/pages/Crawler";
import Streams from "@/pages/Streams";
import AppStreams from "@/pages/AppStreams";
import AuthCallback from "@/pages/AuthCallback";
import Pricing from "@/pages/Pricing";
import Product from "@/pages/Product";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";
import NotFound from "@/pages/NotFound";
import AdminRoles from "@/pages/AdminRoles";
import AdminChurn from "@/pages/AdminChurn";
import StationLanding from "@/pages/StationLanding";
import AppPlaceholder from "@/pages/AppPlaceholder";
import VerifiedLeaderboard from "@/pages/VerifiedLeaderboard";
import { BlogList, BlogPost } from "@/pages/Blog";
import { RateCardPage, SchedulePage, ProfilePage, AdLogPage } from "@/pages/app/StationTabs";
import { AppShell } from "@/components/AppShell";
import { CommandPalette } from "@/components/CommandPalette";
import { PlayerProvider } from "@/context/PlayerContext";
import { PlayerBar } from "@/components/PlayerBar";
import { CookieNotice } from "@/components/CookieNotice";

function Guard({ children, roles }) {
  const { isLoggedIn, loading, user } = useAuth();
  // F-119: preserve the URL the user tried to reach so that after auth we
  // send them back to it instead of the default landing page.
  const loc = useLocation();
  if (loading) return null;
  if (!isLoggedIn) {
    const nextParam = loc.pathname + loc.search + loc.hash;
    return <Navigate to={`/login?next=${encodeURIComponent(nextParam)}`} replace />;
  }
  // RBAC (Feb 2026): `role: "admin"` was decomposed into an `admin_profile`
  // with three scopes. A Guard that names `roles: ["admin"]` treats ANY
  // active admin_profile as a match (specific scope enforcement happens at
  // the API layer). Business roles (station_manager/advertiser/brand) still
  // match against the flat `user.role` field.
  if (roles && user) {
    const isAdmin = !!(user.admin_profile && user.admin_profile.is_active
                       && (user.admin_profile.roles || []).length > 0);
    const businessMatch = roles.includes(user.role);
    const adminMatch = roles.includes("admin") && isAdmin;
    if (!businessMatch && !adminMatch) return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── PUBLIC / ACQUISITION ─────────────────────────────────────── */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login mode="login" />} />
      <Route path="/register" element={<Login mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify/:id" element={<VerifyCertificate />} />
      {/* Public SEO-optimised leaderboard + blog. */}
      <Route path="/verified" element={<Navigate to="/verified/this-month" replace />} />
      <Route path="/verified/:period" element={<VerifiedLeaderboard />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/listen" element={<Listen />} />
      <Route path="/tv" element={<WatchTV />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/crawler" element={<Crawler />} />
      <Route path="/streams" element={<Streams />} />
      <Route path="/product" element={<Product />} />
      <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
      <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
      <Route path="/stations/:stationId" element={<StationLanding />} />
      {/* Legacy singular links (embed widget verify_url, shared URLs) — F-102 */}
      <Route path="/station/:stationId" element={<StationLanding />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/pricing" element={<Pricing />} />

      {/* ── CONVERSION (gated but no shell) ──────────────────────────── */}
      <Route path="/claim" element={<Guard roles={["station_manager", "advertiser", "brand", "admin"]}><ClaimStation /></Guard>} />
      <Route path="/admin/roles" element={<Guard roles={["admin"]}><AdminRoles /></Guard>} />
      <Route path="/admin/churn" element={<Guard roles={["admin"]}><AdminChurn /></Guard>} />

      {/* ── SUBSCRIBER APP (persistent shell at /app/*) ──────────────── */}
      <Route path="/app" element={<Guard><AppShell /></Guard>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<StationDash />} />
        <Route path="adverify" element={<AdVerify />} />
        <Route path="planning" element={<PlanningTool />} />
        <Route path="map" element={<MapDashboard />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Profile />} />
        <Route path="security" element={<Profile tab="security" />} />
        {/* Phase-2 placeholders — sidebar still resolves, no 404 */}
        <Route path="rate-card" element={<RateCardPage />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="ad-log" element={<AdLogPage />} />
        <Route path="streams" element={<AppStreams />} />
        <Route path="reports" element={<AppPlaceholder />} />
        <Route path="brand-intel" element={<AppPlaceholder />} />
        <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
      </Route>

      {/* ── ADMIN (own shell) ────────────────────────────────────────── */}
      <Route path="/admin" element={<Guard roles={["admin"]}><AdminPanel /></Guard>} />

      {/* ── LEGACY REDIRECTS — keep old links + SEO + email links alive ─ */}
      {/* /dashboard doubles as the Google OAuth callback (fragment #session_id). */}
      <Route path="/dashboard" element={<AuthCallback />} />
      <Route path="/campaigns" element={<Navigate to="/app/adverify" replace />} />
      <Route path="/plan" element={<Navigate to="/app/planning" replace />} />
      <Route path="/map" element={<Navigate to="/app/map" replace />} />
      <Route path="/profile" element={<Navigate to="/app/settings" replace />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      {/* F-118 / F-024: skip-link for keyboard/screen-reader users. */}
      <a href="#main" className="ws-skip-link" data-testid="skip-to-content">
        Skip to main content
      </a>
      <AuthProvider>
        <CurrencyProvider>
          <DesignProvider tone="dark">
            <BrowserRouter>
              <PlayerProvider>
                <ErrorBoundary tone="dark">
                  <main id="main" tabIndex={-1} style={{ outline: "none" }}>
                    <AppRoutes />
                  </main>
                </ErrorBoundary>
                <PlayerBar />
              </PlayerProvider>
              <CommandPalette />
              <CookieNotice />
              <Toaster position="top-right" theme="dark" richColors closeButton
                       toastOptions={{ style: { fontFamily: "'Inter',sans-serif" } }} />
            </BrowserRouter>
          </DesignProvider>
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
